import { ArrowRight } from 'lucide-react';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

const AVATAR_STYLE = 'bg-primary/10 text-primary';

function titleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

interface Party {
    id: number;
    name: string;
    business_type: string;
    status: string;
}

interface RelatedPartiesCardProps {
    parties: Party[];
    onManage?: () => void;
}

export function RelatedPartiesCard({ parties, onManage }: RelatedPartiesCardProps) {
    return (
        <Card className="lg:col-span-2 gap-0 rounded-xl border-border/70 py-0 shadow-xs">
            <CardHeader className="flex! items-start justify-between gap-2 px-5 pt-5 pb-3">
                <div className="space-y-1">
                    <CardTitle className="text-base font-semibold">
                        Related Parties
                    </CardTitle>
                    <CardDescription className="text-xs">
                        Suppliers, distributors &amp; partners.
                    </CardDescription>
                </div>
                <button
                    type="button"
                    onClick={onManage}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                    Manage
                    <ArrowRight className="h-3.5 w-3.5" />
                </button>
            </CardHeader>
            <CardContent className="px-3 pb-2">
                {parties.slice(0, 4).map((party) => (
                    <div
                        key={party.id}
                        className="flex items-center gap-3 border-b border-border/40 px-2 py-3.5 last:border-0"
                    >
                        <div
                            className={cn(
                                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                                AVATAR_STYLE,
                            )}
                        >
                            {party.name.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-foreground">
                                {party.name}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">{titleCase(party.business_type)}</p>
                        </div>
                        <span
                            className={cn(
                                'shrink-0 rounded-full px-3 py-1 text-xs font-semibold',
                                party.status === 'active'
                                    ? 'bg-success/10 text-success'
                                    : 'bg-muted text-muted-foreground',
                            )}
                        >
                            {titleCase(party.status)}
                        </span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
